import React, {useEffect, useMemo} from 'react';
import {useI18nContext} from "../../contexts/I18nContext";
import {useStyles} from "./ArticleDelivery.style";
import Box from "../../ui_components/Box/Box";
import Width from "../../ui_components/common/enums/Width";
import Alignment from "../../ui_components/common/enums/Alignment";
import Button from "../../ui_components/Button/Button";
import {Observer} from "mobx-react-lite";
import ShowPagination from "../../ui_components/ShowPagination/ShowPagination";
import MessageCard from "../../ui_components/MessageCard/MessageCard";
import FastfoodIcon from '@mui/icons-material/Fastfood';
import {useCommonContext} from "../../contexts/CommonContext";
import ArticleDeliveryStore from './ArticleDeliveryStore';
import GeoSelect from "../../shared_components/Selects/GeoSelect/GeoSelect";
import ButtonType from "../../ui_components/Button/ButtonType";
import NewArticleDeliveryButton from "../../shared_components/Buttons/NewArticleDeliveryButton/NewArticleDeliveryButton";
import ArticleDeliveryRow from './ArticleDeliveryRow/ArticleDeliveryRow';
import DeliveryPersonSelect from '../../shared_components/Selects/DeliveryPersonSelect/DeliveryPersonSelect';
import RemittanceStatusSelect from '../../shared_components/Selects/RemittanceStatusSelect/RemittanceStatusSelect';
import DateTimeRangeSelect from '../../ui_components/DateTimeRageSelect/DateTimeRangeSelect';
import CustomRangeRepository, {RangeName} from '../../ui_components/DateTimeRageSelect/CustomRangeRepository';
import AllowAccess from "../../shared_components/Access/AllowAccess/AllowAccess";
import Access from "../../shared_components/Access/AllowAccess/Access";
import ClientContact from '../../entities/ClientContact';
import AgencySelect from "../../shared_components/Selects/AgencySelect/AgencySelect";
import Agency from "../../entities/Agency";
import PrintToDeliverButton from '../../shared_components/Buttons/PrintToDeliverButton/PrintToDeliverButton';
import Checkbox from '../../ui_components/Checkbox/Checkbox';
import FilterInput from "../../shared_components/Inputs/FilterInput/FilterInput";
import ServiceName from '../../entities/ServiceName';
import ShowItemsSelectedAmount from '../../shared_components/Info/ShowItemsSelectedAmount/ShowItemsSelectedAmount';
import ExpandoMenu from '../../ui_components/ExpandoMenu/ExpandoMenu';
import ViewBatchReceiptButton from '../../shared_components/Buttons/ViewBatchReceiptButton/ViewBatchReceiptButton';
import RecalculateOrdersServiceCostButton from '../../shared_components/Buttons/RecalculateOrdersServiceCostButton/RecalculateOrdersServiceCostButton';
import ButtonSize from '../../ui_components/Button/ButtonSize';
import ArticleSelect from '../../shared_components/Selects/ArticleSelect/ArticleSelect';
import Article from '../../entities/Article';
import UpdateDeliveryOrdersStatusButton from '../../shared_components/Buttons/UpdateDeliveryOrdersStatusButton/UpdateDeliveryOrdersStatusButton';
import DeliveryOrderType from '../../model/DeliveryOrderType';
import DeliveryStep from '../../entities/DeliveryStep';
import ChooseDeliveryFlowLink from '../../shared_components/Info/ChooseDeliveryFlowLink/ChooseDeliveryFlowLink';
import ProductOrderStatus from '../../entities/productOrder/ProductOrderStatus';
import RemoveProductOrdersButton from '../../shared_components/Buttons/RemoveProductOrdersButton/RemoveProductOrdersButton';

const ArticleDelivery: React.FC = () => {
    const commonStore = useCommonContext();
    const i18n = useI18nContext();
    const classes = useStyles();

    const customRanges = CustomRangeRepository.getRanges(i18n);
    const range = customRanges.find(t => t.key === RangeName.LAST_FIFTEEN_DAYS);
    const store = useMemo<ArticleDeliveryStore>(() =>
        new ArticleDeliveryStore(commonStore, range || customRanges[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , []);

    useEffect(() => {
        store.getElements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onArticleRemittanceClick = (articleRemittanceId: string) => {
        store.selectedArticleRemittanceId = articleRemittanceId;
    }

    const onDeliveryFlowSelect = async (data: { deliveryFlow: DeliveryStep[], resetFlow: boolean, warehouseStep?: any | null }) => {
        await store.setArticleDeliveryFlow([store.selectedArticleRemittanceId], data.deliveryFlow, data.resetFlow, data.warehouseStep);
        await store.getElements();
    }

    const onDeliveryFlowBulkSelect = async (data: { deliveryFlow: DeliveryStep[], resetFlow: boolean, warehouseStep?: any | null }) => {
        await store.setArticleDeliveryFlow(store.selectedArticleRemittanceIds, data.deliveryFlow, true, data.warehouseStep);
        await store.getElements();
        store.selectedArticleRemittanceIds = [];
    }

    const onContactSelect = (clientContact: ClientContact) => {
        store.updateArticleRemittanceContact(clientContact);
    }

    const onPaginationChange = async (page: number, pageSize: number) => {
        await store.goToPage(page, pageSize);
    }

    const onAgencyChange = (newAgency?: Agency) =>
        store.setAgencyFilter(newAgency);

    const onDateChange = (from: number | undefined, to: number | undefined) => {
        store.setFromFilter(from);
        store.setToFilter(to);
    }

    const onUpdate = async () => {
        await store.getElements();
    }

    const onUpdateAndUnselectAll = async () => {
        store.selectedArticleRemittanceIds = [];
        onMoneyDeliveryOrderToggleSelectAll(false);
        await store.getElements();
    }

    const onMoneyDeliveryOrderToggleSelectAll = (value: boolean) => {
        store.selectedArticleRemittanceIds = value ? store.articleRemittances.map(t => t.id) : [];
    }

    const onArticleDeliveryOrderSelect = (articleRemittanceId: string) => {
        const index = store.selectedArticleRemittanceIds.findIndex(t => t === articleRemittanceId);
        store.selectedArticleRemittanceIds = index >= 0 ?
            store.selectedArticleRemittanceIds.filter(t => t !== articleRemittanceId) :
            [...store.selectedArticleRemittanceIds, articleRemittanceId];
    }

    const onArticleDeliveryOrderToggleSelectAll = (value: boolean) => {
        store.selectedArticleRemittanceIds = value ? store.articleRemittances.map(t => t.id) : [];
    }

    const onArticleChange = (newArticle?: Article) => (store.setArticleFilter(newArticle));

    return (
        <Observer>
            {() => (
                <div className={classes.article_container}>
                    <Box className={classes.filter_container} width={Width.FULL_WIDTH}
                         justifyContent={Alignment.SPACE_BETWEEN}>
                        <FilterInput value={store.filter}
                            placeholder={i18n.translate("MONEY_DELIVERY_ORDER_FILTER_PLACEHOLDER")}
                            hint={'Use coma (,) para buscar por varios números de orden'}
                            onChange={(value) => {
                                store.setFilter(value)
                            }} onEnter={() => store.search()}/>
                        <DateTimeRangeSelect selectedFrom={store.fromFilter} selectedTo={store.toFilter}
                            label={i18n.translate("DATES")}
                            onChange={onDateChange}/>
                        <AllowAccess any={[Access.SEND_ORDERS_TO_AGENCIES]}>
                            <AgencySelect selected={store.agencyFilter} onChange={onAgencyChange}/>
                        </AllowAccess>
                        <AllowAccess any={[Access.LIST_DELIVERY_PERSON]}>
                            <DeliveryPersonSelect
                                onChange={(c) => store.setDeliveryPersonToFilter(c)}
                                selected={store.deliveryPersonFilter}/>
                        </AllowAccess>
                        <RemittanceStatusSelect selectedStatus={store.statusFilter} onChange={(c) => store.setStatusToFilter(c)}/>
                        <GeoSelect selected={store.geoFilter} onChange={(value) => {
                            store.setGeoFilter(value);
                        }}/>
                        <ArticleSelect
                            fullWidth
                            label={'Artículo'}
                            selected={store.articleFilter}
                            onChange={onArticleChange}/>
                        <div className={classes.filter_buttons_container}>
                            <Button
                                onClick={() => {
                                    store.search()
                                }}
                                type={ButtonType.PRIMARY}
                                className={classes.search_btn}
                                text={i18n.translate("SEARCH")}
                            />
                            <Button
                                onClick={() => {
                                    store.clearFilter()
                                }}
                                className={classes.search_btn}
                                text={i18n.translate("CLEAN")}
                            />
                        </div>
                    </Box>
                    <div className={classes.article_table}>
                        <Box className={classes.tools_container}
                             width={Width.FULL_WIDTH}
                             justifyContent={Alignment.END}>
                            <div className={classes.select_container}>
                                <Checkbox text={''}
                                    selected={store.selectedArticleRemittanceIds.length === store.articleRemittances.length}
                                    onChange={onArticleDeliveryOrderToggleSelectAll} className={classes.select_all_checkbox}/>
                                    <ExpandoMenu openToRight={true} disabled={store.haveNotSelected}>
                                        <>
                                            <ViewBatchReceiptButton
                                                productOrderIds={store.selectedArticleRemittanceIds}
                                                disabled={
                                                    new Set(
                                                        store.articleRemittances
                                                        .filter(t => store.selectedArticleRemittanceIds.includes(t.id))
                                                        .map(t => t.clientId)
                                                    ).size > 1
                                            }/>
                                            <AllowAccess any={[Access.SET_MONEY_DELIVERY_DELIVERY_PERSON]}>
                                                <ChooseDeliveryFlowLink
                                                    articleDeliveryId={store.selectedArticleRemittanceId}
                                                    articleDeliveryStatus={ProductOrderStatus.PENDING}
                                                    deliveryFlow={[]}
                                                    orderByGeosIdProximity={[]}
                                                    haveDeliveryPersonSelected={false}
                                                    disabled={store.disableUpdateBulkDeliveryPerson}
                                                    isButton={true}
                                                    onClose={onUpdate}
                                                    onSelect={onDeliveryFlowBulkSelect}
                                                />
                                            </AllowAccess>
                                            <UpdateDeliveryOrdersStatusButton
                                                disabled={store.disableUpdateBulkStatus}
                                                onSave={onUpdateAndUnselectAll}
                                                type={ButtonType.SECONDARY}
                                                productOrderIds={store.selectedArticleRemittanceIds}
                                                deliveryOrderType={DeliveryOrderType.ARTICLE_DELIVERY}
                                            />
                                            <AllowAccess any={[Access.RECALCULATE_PRODUCT_ORDERS_SERVICE_COST]}>
                                                <RecalculateOrdersServiceCostButton
                                                    size={ButtonSize.SMALL}
                                                    productOrdersIds={store.selectedArticleRemittanceIds}
                                                    showText
                                                    type={ButtonType.SECONDARY}
                                                />
                                            </AllowAccess>
                                            <PrintToDeliverButton productOrderIds={store.selectedArticleRemittanceIds}
                                                service={ServiceName.ARTICLE_DELIVERY}
                                                disabled={store.selectedArticleRemittanceIds.length === 0}
                                                showText={true}
                                                type={ButtonType.SECONDARY}
                                            />
                                            <RemoveProductOrdersButton
                                                size={ButtonSize.SMALL}
                                                onSave={onUpdateAndUnselectAll}
                                                productOrdersIds={store.selectedArticleRemittanceIds}
                                                showText
                                                type={ButtonType.SECONDARY}
                                            />
                                        </>
                                    </ExpandoMenu>
                                <ShowItemsSelectedAmount
                                    amount={store.selectedArticleRemittanceIds.length}
                                    elementLabel={'envío'}
                                    elementLabelPlural={'envíos'}
                                />
                            </div>
                            
                            <NewArticleDeliveryButton type={ButtonType.SECONDARY}/>
                        </Box>
                        {!store.isEmpty &&
                            <>
                                <ShowPagination onChange={onPaginationChange}
                                                elementLabel={i18n.translate("ARTICLE_REMITTANCES")}
                                                pagination={store.pagination}/>
                                <div className={classes.elements_container}>
                                    {store.articleRemittances.map((articleRemittance, i) => (
                                        <ArticleDeliveryRow
                                            filter={store.filter}
                                            from={store.fromFilter ?? 0}
                                            to={store.toFilter ?? 0}
                                            onArticleDeliveryOrderClick={onArticleRemittanceClick}
                                            onArticleDeliveryOrderSelect={onArticleDeliveryOrderSelect}
                                            onDeliveryFlowSelect={onDeliveryFlowSelect}
                                            onContactSelect={onContactSelect}
                                            articleDeliveryOrder={articleRemittance} key={i}
                                            selectedDeliveryOrderIds={store.selectedArticleRemittanceIds}
                                            onUpdate={onUpdate}
                                        />
                                    ))}
                                </div>
                                <ShowPagination
                                    onChange={onPaginationChange}
                                    elementLabel={i18n.translate("ARTICLE_REMITTANCES")}
                                    pagination={store.pagination}/>
                            </>
                        }
                    </div>
                    {store.isEmpty &&
                    <MessageCard icon={FastfoodIcon} title={i18n.translate("NO_ARTICLE_DELIVERY_TITLE")}
                                subtitle={i18n.translate("NO_ARTICLE_DELIVERY_SUBTITLE")}/>}
                </div>
            )}
        </Observer>
    )
}

export default ArticleDelivery;
